import { nanoid } from 'nanoid';
import { createLogger } from 'app/logger';
import { getConfig } from 'app/env';
import { getInstallationId, getProductVersion } from 'app/platform';
import { AnalyticsAdapter } from './analytics-adapter';

const log = createLogger('analytics');

const anonymousUserId = '_anonymous_';
const sessionId = nanoid(); // beware, uuid was barfing with a 'global is not defined' error

export class AnalyticsManager {
  private adapters: AnalyticsAdapter[] = [];
  private contextData: Record<string, any> = {};

  constructor() {
    // bind all methods to this
    this.gatherProperties = this.gatherProperties.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.trackEventInAdapters = this.trackEventInAdapters.bind(this);
    this.trackPageInAdapters = this.trackPageInAdapters.bind(this);
    this.identifyInAdapters = this.identifyInAdapters.bind(this);
    this.setContextData = this.setContextData.bind(this);
  }

  get userId(): string | null {
    return (this.contextData.accountData?.userId as string) ?? null;
  }

  public addAdapter(service: AnalyticsAdapter) {
    this.adapters.push(service);
  }

  public trackEvent(eventName: string, eventProperties: any = {}) {
    const properties = this.gatherProperties(eventProperties);
    this.trackEventInAdapters(eventName, properties);
  }

  public trackPage = (pageName: string, pageData?: any) => {
    const properties = this.gatherProperties(pageData);
    this.trackPageInAdapters(pageName, properties);
  };

  public startTimeEvent(eventName: string, eventProperties?: any) {
    const start = new Date().getTime();
    return (extraProperties = {}) => {
      const end = new Date().getTime();
      const duration = end - start;
      const props = {
        ...eventProperties,
        ...extraProperties,
        $duration: duration,
      };
      this.trackEvent(eventName, props);
    };
  }

  public autoTrackBefore<T>(
    fn: (...args: T[]) => void,
    eventName: string,
    ...eventArgs: any[]
  ) {
    return (...args: T[]) => {
      this.trackEvent(eventName, ...eventArgs);
      fn(...args);
    };
  }

  public autoTrackAfter<T>(
    fn: (...args: T[]) => void,
    eventName: string,
    ...eventArgs: any[]
  ) {
    return (...args: T[]) => {
      fn(...args);
      this.trackEvent(eventName, ...eventArgs);
    };
  }

  // TODO: check exactly what context data we require
  public setContextData(data: any) {
    this.contextData = data;

    log.info('setContextData', {
      userId: data.accountData?.userId,
      apiEnv: data.globalConfig?.apiEnv,
      data,
    });

    if (this.userId) {
      this.identifyInAdapters(this.userId);
    } else if (getConfig('analytics.enableAnonymous', true as any)) {
      this.identifyInAdapters(anonymousUserId);
    }
  }

  private identifyInAdapters(userId: string) {
    this.adapters.forEach(service => {
      if (!service.isEnabled) {
        return;
      }
      service.identify(userId);
    });
  }

  private trackPageInAdapters(page: string, pageData?: any) {
    this.adapters.forEach(service => {
      if (!service.isEnabled) {
        return;
      }
      service.page(page, pageData);
    });
  }

  private trackEventInAdapters(eventName: string, data?: any) {
    this.adapters.forEach(service => {
      if (!service.isEnabled) {
        return;
      }
      service.track(eventName, data);
    });
  }

  public gatherProperties(eventProperties: any = {}) {
    const date = Date.now();
    // const releaseChannel = 'dev_build'; // this is not
    const properties = {
      email: this.contextData?.accountData?.email, // redundant data, but makes live event feed easier to browse
      server_env: this.contextData?.globalConfig?.apiEnv,
      installation_id: getInstallationId(),
      session_id: sessionId,
      $ae_session: sessionId,
      event_timestamp: date,
      // Note, the proper Mixpanel key here should be $app_version_string, but this value seems to get
      // stomped by Segment even when we provide here, so using a more distinctly named key for our purposes.
      jw_app_version: getProductVersion(),
      product_flavor: getConfig('productFlavor'),
      ...eventProperties,
    };
    if (!this.userId) {
      properties['anonymous_track'] = true;
    }

    return properties;
  }
}
